"use client";

import { useState, forwardRef, useImperativeHandle } from "react";

import styles from "../Stock Transfer/css/stockTransfer.module.css";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import Pagination from "../../../components/Pagination";
import Status from "../../../components/Status";
import FilterButtons from "../../../components/universal-filter-tabs.jsx";
import StockRequestModal from "../Stock Request/components/StockRequestModal.jsx";

// Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
const formatDateDMY = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Reusable stock table with optional left/right controls above filters.
// Delegates all modal UI/logic to StockRequestModal.
const StockTable = forwardRef(function StockTable(
  {
    rows,
    leftContent,
    rightContent,
    showRightContent = true,
    controlsClassName,
    onSubmitRequest,
    onApprove,
    onDecline,
    mode,
    branches = [],
    activeSegment,
    onSegmentChange,
    currentPage,
    totalPages,
    onPageChange,
  },
  ref
) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState("request");
  const [selectedRow, setSelectedRow] = useState(null);

  // Imperative API used by the page (Request Stock button and close after submit)
  useImperativeHandle(ref, () => ({
    openRequestModal: () => {
      setSelectedRow(null);
      setModalContext("request");
      setModalOpen(true);
    },
    closeRequestModal: () => {
      setModalOpen(false);
    },
  }));

  const firstColHeader =
    activeSegment === "Dispensary"
      ? "Drug Name"
      : activeSegment === "Clinic"
      ? "Item Name"
      : "Product Name";

  const columns = [
    { header: firstColHeader, accessor: "drugName" },
    { header: "Type", accessor: "type" },
    { header: "Current Quantity", accessor: "currentQty" },
    { header: "Requested Quantity", accessor: "requestedQty" },
    { header: "Transfer To", accessor: "transferTo" },
    { header: "Transfered from", accessor: "transferFrom" },
    {
      header: "Status",
      accessor: "status",
      render: (value) => <Status status={value} variant="process" />,
    },
    { header: "Request Date", accessor: "requestDate", render: (value) => formatDateDMY(value) },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <Button
          variant="primary"
          className={styles.viewBtn}
          onClick={() => {
            setSelectedRow(row);
            setModalContext("view");
            setModalOpen(true);
          }}
        >
          View More
        </Button>
      ),
    },
  ];

  // Match Stock Transfer behaviour: hide the "Type" column except for Clinic
  const visibleColumns =
    activeSegment === "Clinic" ? columns : columns.filter((c) => c.accessor !== "type");

  return (
    <div className={styles.container}>
      {/* Controls Row: left and right of the filter tabs */}
      <div
        className={controlsClassName || ""}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: controlsClassName ? undefined : "space-between",
          gap: controlsClassName ? undefined : 12,
          marginBottom: controlsClassName ? undefined : 12,
        }}
      >
        <div style={{ minWidth: controlsClassName ? undefined : 200 }}>{leftContent}</div>
        <FilterButtons
          buttons={["Dispensary", "Front Shop", "OTC", "Clinic"]}
          activeButton={activeSegment}
          onButtonClick={onSegmentChange}
          className={`${styles.segmentTabs} ${controlsClassName ? styles.segmentTabsCompact : ""}`}
        />
        <div>{showRightContent ? rightContent : null}</div>
      </div>

      <Table columns={visibleColumns} data={rows} />
      <div className={styles.paginationContainer}>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
      </div>

      <StockRequestModal
        isOpen={modalOpen}
        context={modalContext}
        selectedRow={selectedRow}
        activeSegment={activeSegment}
        mode={mode}
        branches={branches}
        onSubmitRequest={onSubmitRequest}
        onApprove={onApprove}
        onDecline={onDecline}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
});

export default StockTable;